import { FunctionComponent, memo, useContext, useState } from "react";
import { BoxContent, BoxHeader, BoxHeading, BoxSubheading } from "@edgetier/client-components";
import { TimePeriodGroupBy } from "@edgetier/watchtower-types";
import { faUndo } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@edgetier/components";
import MetricValuesChartDateRange from "../anomaly-metric-values-chart/metric-values-chart-date-range";
import AnomalyPhraseCountsChartFilters from "./anomaly-phrase-counts-chart-filters";
import AnomalyPhraseCountsValue from "./anomaly-phrase-counts-value/anomaly-phrase-counts-value";
import PhraseCountsChart from "components-for/anomalies/phrase-counts-chart";
import useChartDrag from "hooks-for/anomalies/use-chart-drag";
import { getRangeOptions } from "./anomaly-phrase-counts-chart.utilities";
import { IAnomalyPhraseCountsChartFilters } from "./anomaly-phrase-counts-chart-filters/anomaly-phrase-counts-chart-filters.types";
import { INTERACTIONS_TABLE_PREFIX } from "constants/interactions";
import { IProps } from "./anomaly-phrase-counts-chart.types";
import { ExploreContext } from "components-for/explore/explore-context/explore-context";
import "./anomaly-phrase-counts-chart.scss";
import { RANGE_DAYS } from "./anomaly-phrase-counts-chart.constants";

/**
 * Chart the number of interactions affected by an anomaly.
 * @param props.anomalyId            The ID of the anomaly whose data is being displayed.
 * @param props.anomalyEndDateTime   When the anomaly ended (it may be null if it's still occurring).
 * @param props.anomalyStartDateTime When the anomaly first appeared.
 * @param props.dateTime             Date of the anomaly first occurring.
 * @param props.defaultTimeGroupBy   Optional time group visible instead of days
 * @param props.filterRange          Whether the user is allowed to change the chart range or not.
 * @param props.highlightRange       An area of the chart that should be highlighted.
 * @param props.showInteractionsTotal Should the total number of interactions be visible
 * @param props.setDateFilterRange   A function to set the date range selected.
 * @returns                          Chart and filters.
 */
const AnomalyPhraseCountsChart: FunctionComponent<IProps> = ({
    anomalyId,
    anomalyEndDateTime,
    anomalyStartDateTime,
    dateTime,
    defaultTimeGroupBy = TimePeriodGroupBy.Days,
    filterRange = true,
    showInteractionsTotal = false,
    highlightRange,
}) => {
    const { setChartGroupBy } = useContext(ExploreContext);

    const [filters, setFilters] = useState<IAnomalyPhraseCountsChartFilters>({
        groupBy: defaultTimeGroupBy,
        proportion: false,
        dayRange: RANGE_DAYS[0],
        ...getRangeOptions(dateTime, RANGE_DAYS[0]),
    });

    const chartSelectionStartDateTime = filterRange ? filters.startDateTime : anomalyStartDateTime;
    const chartSelectionEndDateTime = filterRange ? filters.endDateTime : anomalyEndDateTime!;

    const { filterDateRange, resetChartFilter, mouseEventCallbacks } = useChartDrag({
        defaultValues: {
            startDateTime: chartSelectionStartDateTime,
            endDateTime: chartSelectionEndDateTime,
        },
        timePeriodGroupBy: filters.groupBy,
        prefixOptions: {
            prefix: INTERACTIONS_TABLE_PREFIX,
            isPrefixOptional: false,
        },
    });

    /**
     * Update filters and clear any selection
     */
    const onChangeFilters = (formFilters: IAnomalyPhraseCountsChartFilters) => {
        const { dayRange, ...otherFilters } = formFilters;

        setFilters({
            ...otherFilters,
            ...getRangeOptions(dateTime, dayRange!),
        });
        setChartGroupBy(formFilters.groupBy);
        resetChartFilter();
    };

    return (
        <div className="anomaly-phrase-counts-chart">
            <BoxHeader>
                <div>
                    <BoxHeading>
                        Interactions&ensp;
                        {showInteractionsTotal && (
                            <AnomalyPhraseCountsValue
                                proportion={filters.proportion}
                                startDateTime={chartSelectionStartDateTime}
                                endDateTime={chartSelectionEndDateTime}
                            />
                        )}
                    </BoxHeading>
                    <BoxSubheading>Count of related interactions</BoxSubheading>
                </div>

                <AnomalyPhraseCountsChartFilters
                    filterRange={filterRange}
                    initialValues={filters}
                    onChange={onChangeFilters}
                />
            </BoxHeader>
            <BoxContent>
                <div className="anomaly-phrase-counts-chart__filter-info">
                    <MetricValuesChartDateRange
                        firstDate={filterDateRange[0].date}
                        secondDate={filterDateRange[1].date}
                        timePeriodGroup={filters.groupBy}
                    />
                    <Button
                        className={"anomaly-phrase-counts-chart__filter-reset"}
                        disabled={filterDateRange.some(({ activeLabel }) => activeLabel === null)}
                        icon={faUndo}
                        onClick={() => resetChartFilter()}
                        isIconOnly
                    />
                </div>

                <div className="anomaly-phrase-counts-chart__chart">
                    <PhraseCountsChart
                        anomalyId={anomalyId}
                        highlightEndDateTime={anomalyEndDateTime}
                        highlightRange={highlightRange}
                        highlightStartDateTime={anomalyStartDateTime}
                        filterRange={filterDateRange}
                        mouseCallbacks={mouseEventCallbacks}
                        {...filters}
                        {...(!filterRange
                            ? { startDateTime: anomalyStartDateTime, endDateTime: anomalyEndDateTime }
                            : {})}
                    />
                </div>
            </BoxContent>
        </div>
    );
};

export default memo(AnomalyPhraseCountsChart);
